import {trimGuid} from '@packages/utilities/guid';
import {createRulesFromConfig} from './default-styles';
import {Component, PseudoFunction} from '@packages/systems/style/selectors';
import type {
  StyleRulesByBreakpointId,
  ConditionalDefaultStyleDeclaration,
  DefaultStyleDeclaration,
} from '@packages/systems/style/types';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {cssifyName} from '@packages/systems/core/utils/TextUtils';
import {VariantStyleDeclaration} from '@packages/systems/style/types';
import {
  parseVariantStateKey,
  VariantStateKey,
} from '@packages/systems/style-legacy/selectors';
import {Expressions, EvalEnv} from '@packages/systems/wfdl';
import type {ExpressionPath} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';

const LOCAL_STYLE_PREFIX = 'node';

const addSymbolId = (namespace: string, symbolId?: string): string =>
  `${namespace}${symbolId ? `-${trimGuid(String(symbolId), 8)}` : ''}`;

/**
 * Creates a css selector namespace from an id for local style sheets.
 */
export const generateNamespace = (id: string, symbolId?: string): string => {
  // These ids can be a guid or a label id (incremented for uniqueness). These
  // selectors need to be valid css (otherwise the browser will throw them away),
  // so `cssifyName` cssifies the id.
  const cssifiedId = cssifyName(id) || ''; // This `|| ''` is here to appease flow

  return `w-${LOCAL_STYLE_PREFIX}-${addSymbolId(cssifiedId, symbolId)}`;
};

/**
 * Creates a CSS selector namespace from an id and variant key for local style sheets.
 */
export const generateNamespaceWithVariantKey = (
  variantKey: VariantStateKey
): string => {
  const [_, variantOptionId] = parseVariantStateKey(variantKey);
  return `w-variant-${variantOptionId}`;
};

const emptyObject: Record<string, any> = {};

const createLocalStyleRulesFromConditionalConfig = (
  namespace: string,
  config: ConditionalDefaultStyleDeclaration,
  rulesByBreakpointId: StyleRulesByBreakpointId
): StyleRulesByBreakpointId => {
  for (const [conditionalKey, valueConfig] of Object.entries(config)) {
    for (const valueEntry of Object.entries(valueConfig)) {
      const valueKey: string = valueEntry[0];
      const ruleConfig: DefaultStyleDeclaration = valueEntry[1] as any;
      // Create style rules from a style config using the conditional
      // class name as the base selector component.
      createRulesFromConfig(
        [Component.Id(`${namespace}--${conditionalKey}-${valueKey}`)],
        ruleConfig,
        rulesByBreakpointId,
        false
      );
    }
  }

  return rulesByBreakpointId;
};

const createLocalStyleRulesFromVariantsConfig = (
  variantsConfig: VariantStyleDeclaration,
  nodeId: string,
  attrId: string | undefined,
  componentId: string | undefined,
  rulesByBreakpointId: StyleRulesByBreakpointId
): StyleRulesByBreakpointId => {
  if (!componentId) return rulesByBreakpointId;

  const namespace = generateNamespace(nodeId, componentId);
  const baseSelector = attrId
    ? [Component.Id(attrId), Component.Class(namespace)]
    : [Component.Id(namespace)];

  for (const [variantKey, ruleConfig] of Object.entries(variantsConfig)) {
    createRulesFromConfig(
      [
        ...baseSelector,
        Component.PseudoFunction(
          PseudoFunction.Where,
          Component.Class(
            generateNamespaceWithVariantKey(variantKey as VariantStateKey)
          )
        ),
      ],
      ruleConfig,
      rulesByBreakpointId,
      false
    );
  }

  return rulesByBreakpointId;
};

const createLocalStyleRules = (
  style: any,
  nodeId: string,
  attrId?: string,
  symbolId?: string
): StyleRulesByBreakpointId => {
  if (style == null) {
    return emptyObject;
  }
  const rulesByBreakpointId: StyleRulesByBreakpointId = {};
  const {base, conditional, variants} = style;
  const namespace = generateNamespace(nodeId, symbolId);

  if (base) {
    const selector = attrId
      ? [Component.Id(attrId), Component.Class(namespace)]
      : [Component.Id(namespace)];
    createRulesFromConfig(selector, base, rulesByBreakpointId, false);
  }

  if (variants) {
    createLocalStyleRulesFromVariantsConfig(
      variants,
      nodeId,
      attrId,
      symbolId,
      rulesByBreakpointId
    );
  }

  if (conditional) {
    createLocalStyleRulesFromConditionalConfig(
      namespace,
      conditional,
      rulesByBreakpointId
    );
  }

  return rulesByBreakpointId;
};

export const createLocalStyleRulesFromJsonNode = (
  node: JSONNode,
  symbolId?: string
): StyleRulesByBreakpointId => {
  let style = null;

  if (node.data && node.data.style) {
    style = node.data.style;
  }

  if (style == null) {
    return emptyObject;
  }

  return createLocalStyleRules(style, node._id, getIdAttr(node), symbolId);
};

function getIdAttr(node: JSONNode): string | undefined {
  if (
    node.data &&
    node.data.attr &&
    node.data.attr.id &&
    typeof node.data.attr.id === 'string'
  ) {
    return node.data.attr.id;
  }
  return undefined;
}

const STYLE_RECORD_PATH: ExpressionPath = [{in: 'Record', at: 'style'}];
const ATTR_RECORD_PATH: ExpressionPath = [{in: 'Record', at: 'attr'}];
const ID_RECORD_PATH: ExpressionPath = [{in: 'Record', at: 'id'}];

export const createLocalStyleRulesFromElement = (
  element: ExpressionElement,
  symbolId?: string
): StyleRulesByBreakpointId => {
  const dataExpr = Expressions.getElementData(element);
  if (!Expressions.isRecord(dataExpr)) return emptyObject;

  try {
    const styleExpr = Expressions.getIn(dataExpr, STYLE_RECORD_PATH);

    if (styleExpr == null) {
      return emptyObject;
    }

    const style = Expressions.evaluate(EvalEnv.emptyEvalEnv, styleExpr);

    if (style == null) {
      return emptyObject;
    }

    const attrExpr = Expressions.getIn(dataExpr, ATTR_RECORD_PATH);
    const idExpr = Expressions.getIn(attrExpr, ID_RECORD_PATH);
    const attrId = (idExpr && Expressions.getText(idExpr)) ?? undefined;

    return createLocalStyleRules(style, element.id, attrId, symbolId);
  } catch {
    return emptyObject;
  }
};
